"use client";

import { Loader2, Mail, RefreshCw, Trash2, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackSharing } from "@/lib/analytics";
import type { SharedListInfo } from "@/lib/types";
import { sharingSchemas, validate } from "@/lib/validation";

interface ShareListSectionProps {
  listId: string;
  listName: string;
  shares: SharedListInfo[];
  isLoadingShares?: boolean;
  onShareAdded: () => void;
  onShareRemoved: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export function ShareListSection({
  listId,
  listName: _listName,
  shares,
  isLoadingShares = false,
  onShareAdded,
  onShareRemoved,
  onLoadingChange,
}: ShareListSectionProps) {
  const t = useTranslations("lists");
  const [email, setEmail] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleShare = async () => {
    const trimmedEmail = email.trim();

    // Validate email
    const validationResult = validate(sharingSchemas.email, trimmedEmail);
    if (!validationResult.success) {
      // Extract validation key (e.g., "validation.invalidEmail" -> "invalidEmail")
      const errorKey = validationResult.error?.replace(/^validation\./, "") || "invalidEmail";
      toast.error(t(errorKey as any));
      return;
    }

    setIsSharing(true);
    onLoadingChange?.(true);

    try {
      const response = await fetch(`/api/lists/${listId}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(t(data.error as any) || t("failedToShare"));
        return;
      }

      trackSharing("invitation_sent", { listId });

      toast.success(t("invitationSent", { email }), {
        description: data.invitation.emailSent ? t("emailDelivered") : t("emailNotConfigured"),
      });

      setEmail("");
      onShareAdded();
    } catch (err) {
      toast.error(t("failedToShare"), {
        description: t("pleaseTryAgain"),
      });
      console.error(err);
    } finally {
      setIsSharing(false);
      onLoadingChange?.(false);
    }
  };

  const handleResend = async (invitationId: string, recipientEmail: string) => {
    setResendingId(invitationId);
    onLoadingChange?.(true);

    try {
      const response = await fetch(`/api/lists/${listId}/share/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invitationId }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(t(data.error as any) || t("failedToResend"));
        return;
      }

      toast.success(t("invitationResent"), {
        description: t("emailSentTo", { email: recipientEmail }),
      });
      // No need to re-fetch on resend - the share already exists
    } catch (err) {
      toast.error(t("failedToResend"), {
        description: t("pleaseTryAgain"),
      });
      console.error(err);
    } finally {
      setResendingId(null);
      onLoadingChange?.(false);
    }
  };

  const handleUnshare = async (invitationId: string, recipientEmail: string) => {
    setRemovingId(invitationId);
    onLoadingChange?.(true);

    try {
      const response = await fetch(`/api/lists/${listId}/share`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invitationId }),
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(t(data.error as any) || t("failedToRemove"));
        return;
      }

      // Update UI immediately before showing toast
      onShareRemoved();

      toast.success(t("shareRemoved"), {
        description: t("noLongerHasAccess", { email: recipientEmail }),
      });
    } catch (err) {
      toast.error(t("failedToRemove"), {
        description: t("pleaseTryAgain"),
      });
      console.error(err);
    } finally {
      setRemovingId(null);
      onLoadingChange?.(false);
    }
  };

  const isAnyLoading = isSharing || resendingId !== null || removingId !== null;

  return (
    <div className="border-t border-border pt-4 sm:pt-6">
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-primary" />
            <h3>{t("shareList")}</h3>
          </div>
          <Label htmlFor="share-email">{t("inviteByEmail")}</Label>
          <div className="flex gap-2 mt-2">
            <Input
              id="share-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("emailPlaceholder")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleShare();
                }
              }}
              disabled={isAnyLoading}
            />
            <Button
              onClick={handleShare}
              disabled={isAnyLoading || !email.trim()}
              className="flex-shrink-0 gap-1"
            >
              {isSharing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  {t("send")}
                </>
              )}
            </Button>
          </div>
        </div>

        {(shares.length > 0 || isLoadingShares) && (
          <div>
            <h4 className="mb-2">{t("sharedWith")}</h4>
            <div className="space-y-2">
              {isLoadingShares ? (
                // Loading skeleton
                <>
                  {[1, 2].map((i) => (
                    <div
                      key={`skeleton-${i}`}
                      className="flex items-center justify-between gap-2 p-3 bg-secondary/50 rounded-lg animate-pulse"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-4 h-4 bg-muted rounded flex-shrink-0" />
                        <div className="h-4 bg-muted rounded flex-1 max-w-[200px]" />
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <div className="w-8 h-8 bg-muted rounded" />
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                shares.map((share) => (
                  <div
                    key={share.invitationId}
                    className="flex items-center justify-between gap-2 p-3 bg-secondary/50 rounded-lg"
                  >
                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                      <span className="text-sm truncate">{share.email}</span>
                      {share.accepted ? (
                        <span className="text-xs text-green-600 dark:text-green-500 rounded-full flex-shrink-0">
                          {t("accepted")}
                        </span>
                      ) : (
                        <span className="text-xs text-yellow-600 dark:text-yellow-500 rounded-full flex-shrink-0">
                          {t("pending")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!share.accepted && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResend(share.invitationId, share.email)}
                          disabled={isAnyLoading}
                          title={t("resendInvitation")}
                        >
                          {resendingId === share.invitationId ? (
                            <Loader2 className="size-5 animate-spin text-muted-foreground" />
                          ) : (
                            <RefreshCw className="size-5 text-muted-foreground" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnshare(share.invitationId, share.email)}
                        disabled={isAnyLoading}
                        title={t("removeShare")}
                      >
                        {removingId === share.invitationId ? (
                          <Loader2 className="size-5 animate-spin text-destructive" />
                        ) : (
                          <Trash2 className="size-5 text-destructive" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
